import React, { useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import { ProgressBar } from "primereact/progressbar";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";
import { GitLabProject, ReleaseResult } from "../types/gitlab";
import { gitlabService } from "../services/gitlabService";
import { LinearIssue } from "../services/linearService";
import ReleaseMonitor from "./ReleaseMonitor";

interface ReleaseProcessStepProps {
  selectedProjects: GitLabProject[];
  selectedIssues: LinearIssue[];
  releaseName: string;
  gitTagName: string;
  releaseDescription: string;
  onBack: () => void;
  onStartOver: () => void;
}

const ReleaseProcessStep: React.FC<ReleaseProcessStepProps> = ({
  selectedProjects,
  selectedIssues,
  releaseName,
  gitTagName,
  releaseDescription,
  onBack,
  onStartOver,
}) => {
  const [isCreatingGitLab, setIsCreatingGitLab] = useState(false);
  const [gitlabProgress, setGitlabProgress] = useState(0);
  const [currentProject, setCurrentProject] = useState<string>("");
  const [gitlabResults, setGitlabResults] = useState<ReleaseResult[]>([]);
  const [gitlabComplete, setGitlabComplete] = useState(false);

  const [showForceDialog, setShowForceDialog] = useState(false);
  const [existingTagResults, setExistingTagResults] = useState<ReleaseResult[]>([]);
  const [selectedForReplace, setSelectedForReplace] = useState<Set<number>>(new Set());
  const [isForcing, setIsForcing] = useState(false);

  const [processStarted, setProcessStarted] = useState(false);

  const handleStartProcess = async () => {
    setProcessStarted(true);
    setIsCreatingGitLab(true);
    setGitlabProgress(0);
    setGitlabResults([]);
    setExistingTagResults([]);

    const newResults: ReleaseResult[] = [];
    const existingTags: ReleaseResult[] = [];
    const total = selectedProjects.length;

    for (let i = 0; i < selectedProjects.length; i++) {
      const project = selectedProjects[i];
      setCurrentProject(project.name);
      setGitlabProgress(((i + 1) / total) * 100);

      const result = await gitlabService.createReleaseWithMonitoring(
        project,
        gitTagName,
        releaseDescription
      );

      if (result.tagExists) {
        existingTags.push(result);
      } else {
        newResults.push(result);
      }
    }

    setGitlabResults(newResults);
    setIsCreatingGitLab(false);
    setCurrentProject("");

    if (existingTags.length > 0) {
      setExistingTagResults(existingTags);
      setSelectedForReplace(new Set(existingTags.map((r) => r.project.id)));
      setShowForceDialog(true);
    } else {
      setGitlabComplete(true);
    }
  };

  const handleForceReplace = async () => {
    if (selectedForReplace.size === 0) {
      setShowForceDialog(false);
      setGitlabComplete(true);
      return;
    }

    setIsForcing(true);
    const updatedResults = [...gitlabResults];
    const toReplace = existingTagResults.filter((r) =>
      selectedForReplace.has(r.project.id)
    );

    for (const result of toReplace) {
      const forceResult = await gitlabService.forceCreateTagAndRelease(
        result.project,
        gitTagName,
        releaseDescription
      );
      updatedResults.push(forceResult);
    }

    const skipped = existingTagResults.filter(
      (r) => !selectedForReplace.has(r.project.id)
    );
    for (const result of skipped) {
      updatedResults.push({
        ...result,
        success: false,
        error: "Skipped - tag already exists",
      });
    }

    setGitlabResults(updatedResults);
    setIsForcing(false);
    setShowForceDialog(false);
    setGitlabComplete(true);
  };

  const handleSkipAll = () => {
    const updatedResults = [...gitlabResults];
    for (const result of existingTagResults) {
      updatedResults.push({
        ...result,
        success: false,
        error: "Skipped - tag already exists",
      });
    }
    setGitlabResults(updatedResults);
    setShowForceDialog(false);
    setGitlabComplete(true);
  };

  const handleUseExisting = () => {
    const updatedResults = [...gitlabResults];
    for (const result of existingTagResults) {
      updatedResults.push({
        ...result,
        success: true,
        releaseType: "tag",
        tagExists: false,
      });
    }
    setGitlabResults(updatedResults);
    setShowForceDialog(false);
    setGitlabComplete(true);
  };

  const toggleProjectSelection = (projectId: number) => {
    const newSelected = new Set(selectedForReplace);
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId);
    } else {
      newSelected.add(projectId);
    }
    setSelectedForReplace(newSelected);
  };

  const gitlabSuccessCount = gitlabResults.filter((r) => r.success).length;
  const gitlabFailedCount = gitlabResults.filter((r) => !r.success).length;

  // Pre-process summary
  if (!processStarted) {
    return (
      <>
        <Card className="mb-4">
          <div className="flex align-items-center gap-2 mb-4">
            <i className="pi pi-play text-xl text-primary"></i>
            <h2 className="text-xl font-semibold m-0">Step 3: Release Process</h2>
          </div>

          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="surface-ground p-3 border-round mb-3">
                <div className="flex align-items-center gap-2 mb-2">
                  <i className="pi pi-bookmark text-primary"></i>
                  <span className="font-semibold">Release Name</span>
                </div>
                <div className="text-xl">{releaseName}</div>
              </div>

              <div className="surface-ground p-3 border-round mb-3">
                <div className="flex align-items-center gap-2 mb-2">
                  <i className="pi pi-tag text-primary"></i>
                  <span className="font-semibold">Git Tag Name</span>
                </div>
                <div className="text-xl font-mono">{gitTagName}</div>
              </div>

              {releaseDescription && (
                <div className="surface-ground p-3 border-round mb-3">
                  <div className="flex align-items-center gap-2 mb-2">
                    <i className="pi pi-align-left text-primary"></i>
                    <span className="font-semibold">Description</span>
                  </div>
                  <div className="text-color-secondary">{releaseDescription}</div>
                </div>
              )}
            </div>

            <div className="col-12 md:col-6">
              <div className="surface-ground p-3 border-round mb-3">
                <div className="flex align-items-center gap-2 mb-2">
                  <i className="pi pi-ticket text-primary"></i>
                  <span className="font-semibold">
                    Linear Issues ({selectedIssues.length})
                  </span>
                </div>
                {selectedIssues.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {selectedIssues.slice(0, 8).map((issue) => (
                      <Chip key={issue.id} label={issue.identifier} className="text-xs" />
                    ))}
                    {selectedIssues.length > 8 && (
                      <Chip
                        label={`+${selectedIssues.length - 8} more`}
                        className="text-xs"
                      />
                    )}
                  </div>
                ) : (
                  <Tag value="Skipped" severity="secondary" />
                )}
              </div>

              <div className="surface-ground p-3 border-round">
                <div className="flex align-items-center gap-2 mb-2">
                  <i className="pi pi-folder text-primary"></i>
                  <span className="font-semibold">
                    GitLab Projects ({selectedProjects.length})
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {selectedProjects.slice(0, 8).map((project) => (
                    <Chip key={project.id} label={project.name} className="text-xs" />
                  ))}
                  {selectedProjects.length > 8 && (
                    <Chip
                      label={`+${selectedProjects.length - 8} more`}
                      className="text-xs"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 surface-ground border-round">
            <div className="font-semibold mb-2">Process will:</div>
            <ul className="m-0 pl-4">
              <li>
                Create tag <code className="font-mono">{gitTagName}</code> and
                releases in {selectedProjects.length} GitLab projects
              </li>
            </ul>
          </div>
        </Card>

        <div className="flex justify-content-between">
          <Button
            label="Back to Projects"
            icon="pi pi-arrow-left"
            className="p-button-outlined"
            onClick={onBack}
          />
          <Button
            label="Start Release Process"
            icon="pi pi-play"
            className="p-button-lg p-button-success"
            onClick={handleStartProcess}
          />
        </div>
      </>
    );
  }

  // Process in progress / done
  return (
    <>
      <div className="flex justify-content-between align-items-center mb-4">
        <div className="flex align-items-center gap-2">
          <i className="pi pi-play text-xl text-primary"></i>
          <h2 className="text-xl font-semibold m-0">Release Process Status</h2>
        </div>
        {gitlabComplete && (
          <Button
            label="Start New Release"
            icon="pi pi-refresh"
            className="p-button-outlined"
            onClick={onStartOver}
          />
        )}
      </div>

      {isCreatingGitLab && (
        <Card className="mb-4">
          <div className="flex align-items-center gap-2 mb-3">
            <i className="pi pi-gitlab text-xl text-orange-500"></i>
            <h3 className="text-lg font-semibold m-0">Creating GitLab Releases</h3>
          </div>
          <div className="flex justify-content-between mb-2">
            <span className="text-color-secondary">Processing...</span>
            <span className="font-semibold">{Math.round(gitlabProgress)}%</span>
          </div>
          <ProgressBar value={gitlabProgress} showValue={false} className="mb-2" />
          <div className="text-center text-color-secondary">
            <i className="pi pi-spin pi-spinner mr-2"></i>
            {currentProject}
          </div>
        </Card>
      )}

      {gitlabComplete && gitlabResults.length > 0 && (
        <Card className="mb-4">
          <div className="flex justify-content-between align-items-center mb-3">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-gitlab text-xl text-orange-500"></i>
              <h3 className="text-lg font-semibold m-0">GitLab Releases</h3>
            </div>
            <div className="flex gap-2">
              <Tag value={`${gitlabSuccessCount} successful`} severity="success" />
              {gitlabFailedCount > 0 && (
                <Tag value={`${gitlabFailedCount} failed`} severity="danger" />
              )}
            </div>
          </div>

          <ReleaseMonitor
            results={gitlabResults}
            releaseName={gitTagName}
            onClose={() => {}}
            embedded
          />
        </Card>
      )}

      {gitlabComplete && (
        <Card className="text-center">
          <i className="pi pi-check-circle text-green-500 text-6xl mb-3"></i>
          <h2 className="text-xl font-semibold m-0 mb-2">Release Complete!</h2>
          <p className="text-color-secondary m-0">
            Successfully released {releaseName}
          </p>
          <Button
            label="Start New Release"
            icon="pi pi-refresh"
            className="p-button-lg mt-4"
            onClick={onStartOver}
          />
        </Card>
      )}

      <Dialog
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-exclamation-triangle text-yellow-500"></i>
            <span>Tags Already Exist</span>
          </div>
        }
        visible={showForceDialog}
        onHide={() => !isForcing && handleSkipAll()}
        style={{ width: "600px" }}
        modal
        closable={!isForcing}
        footer={
          <div className="flex gap-2 justify-content-between">
            <Button
              label="Skip All"
              icon="pi pi-times"
              className="p-button-outlined p-button-secondary"
              onClick={handleSkipAll}
              disabled={isForcing}
            />
            <div className="flex gap-2">
              <Button
                label="Use Existing"
                icon="pi pi-check"
                className="p-button-outlined p-button-success"
                onClick={handleUseExisting}
                disabled={isForcing}
              />
              <Button
                label={
                  isForcing
                    ? "Replacing..."
                    : `Replace Selected (${selectedForReplace.size})`
                }
                icon={isForcing ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
                className="p-button-warning"
                onClick={handleForceReplace}
                disabled={isForcing || selectedForReplace.size === 0}
              />
            </div>
          </div>
        }
      >
        <p className="text-color-secondary mb-3">
          The following projects already have a tag named{" "}
          <strong>{gitTagName}</strong>.
        </p>

        <DataTable
          value={existingTagResults}
          className="mb-3"
          size="small"
          scrollable
          scrollHeight="300px"
        >
          <Column
            header=""
            style={{ width: "50px" }}
            body={(rowData: ReleaseResult) => (
              <Checkbox
                checked={selectedForReplace.has(rowData.project.id)}
                onChange={() => toggleProjectSelection(rowData.project.id)}
                disabled={isForcing}
              />
            )}
          />
          <Column
            field="project.name"
            header="Project"
            body={(rowData: ReleaseResult) => (
              <div className="flex align-items-center gap-2">
                <i className="pi pi-folder text-color-secondary"></i>
                <span>{rowData.project.name}</span>
              </div>
            )}
          />
          <Column
            header="Status"
            body={() => (
              <span className="text-yellow-500">
                <i className="pi pi-tag mr-1"></i>
                Tag exists
              </span>
            )}
          />
        </DataTable>

        <div className="text-sm text-color-secondary">
          <div className="mb-2">
            <i className="pi pi-check text-green-500 mr-1"></i>
            <strong>Use Existing:</strong> Monitor the existing tag's pipeline status
          </div>
          <div>
            <i className="pi pi-refresh text-yellow-500 mr-1"></i>
            <strong>Replace Selected:</strong> Delete and recreate selected tags
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ReleaseProcessStep;
